import {
  demographicReportPermissionsMiddleware, checkPermissionsOrRedirect,
} from '@/helpers/routeMiddleware';
import Account from '@/components/settings/account/Account.vue';
import CustomFields from '@/components/settings/CustomFields.vue';
import AcknowledgementSettings from '@/components/settings/AcknowledgementSettings.vue';
import FlsaSettings from '@/components/settings/FlsaSettings.vue';
import AiSettings from '@/components/settings/AiSettings.vue';
import JobPostSettings from '@/components/settings/JobPostSettings.vue';
import JobProfileSettings from '@/components/settings/JobProfileSettings.vue';
import Integrations from '@/components/settings/integrations/Integrations.vue';
import Notifications from '@/components/settings/Notifications.vue';
import OrganizationSettings from '@/components/settings/OrganizationSettings.vue';
import PolicyGuide from '@/components/settings/policyGuide/PolicyGuide.vue';
import DemographicReportPermissions from '@/components/settings/team/views/DemographicReportPermissions.vue';
import DatapeopleAdministrators from '@/components/settings/team/views/DatapeopleAdministrators.vue';
import PlatformPermissions from '@/components/settings/team/views/PlatformPermissions.vue';
import Team from '@/components/settings/team/Team.vue';
import StyleGuide from '@/components/settings/styleGuide/StyleGuide.vue';
import SsoSetup from '@/components/settings/ssoSetup/SsoSetup.vue';
import UserRoles from '@/components/settings/UserRoles.vue';
import Settings from '@/views/Settings.vue';

export const settings = (authDataStore) => ([
  {
    path: '/settings/',
    name: 'settings',
    component: Settings,
    redirect: { name: 'account' },
    meta: { title: 'Settings' },
    children: [
      {
        name: 'account',
        path: 'account',
        meta: { timer: 3000, title: 'Account' },
        component: Account,
      },
      {
        name: 'sso-setup-old',
        path: 'sso-setup',
        redirect: () => ({ name: 'sso-setup' }),
      },
      {
        name: 'notifications',
        path: 'notifications',
        meta: { timer: 3000, title: 'Notifications' },
        component: Notifications,
      },
      {
        name: 'integrations',
        path: 'integrations',
        redirect: () => ({ name: 'ats-integration' }),
      },
      {
        name: 'ats-integration',
        path: 'ats-integration',
        meta: { timer: 3000, title: 'Connect Your ATS' },
        component: Integrations,
      },
      {
        name: 'team',
        path: 'team',
        meta: { timer: 3000, title: 'Team' },
        component: Team,
        redirect: { name: 'administrators' },
        children: [
          {
            name: 'administrators',
            path: 'administrators',
            meta: { timer: 3000, title: 'My JobNav Administrators' },
            component: DatapeopleAdministrators,
          },
          {
            name: 'platform-permissions',
            path: 'platform-permissions',
            meta: { timer: 3000, title: 'Platform Permissions' },
            component: PlatformPermissions,
            beforeEnter: checkPermissionsOrRedirect(authDataStore, ['viewTeam'], 'administrators'),
          },
          {
            name: 'demographic-permissions',
            path: 'demographic-permissions',
            meta: { timer: 3000, title: 'Demographic Report Permissions' },
            component: DemographicReportPermissions,
            beforeEnter: demographicReportPermissionsMiddleware(authDataStore),
          },
        ],
      },
      {
        name: 'usage-settings',
        path: 'usage',
        redirect: (to) => ({ name: 'usage', query: { ...to.query, legacy: 'true' } }),
      },
      {
        name: 'organization-settings',
        path: 'organization-settings',
        component: OrganizationSettings,
        redirect: () => {
          if (authDataStore.checkRbacPermissions(['configureSso'])) {
            return { name: 'sso-setup' };
          }
          if (authDataStore.checkRbacPermissions(['editJobProfileSettings'])) {
            return { name: 'job-profile-settings' };
          }
          if (authDataStore.checkRbacPermissions(['editAcknowledgementsSettings'])) {
            return { name: 'acknowledgements-settings' };
          }
          if (authDataStore.checkRbacPermissions(['editFlsaSettings'])) {
            return { name: 'flsa-settings' };
          }
          return { name: 'settings' };
        },
        children: [
          {
            name: 'sso-setup',
            path: 'sso-setup',
            meta: { timer: 3000, title: 'Organization Settings', parentTitle: 'Settings' },
            component: SsoSetup,
            beforeEnter: checkPermissionsOrRedirect(authDataStore, ['configureSso'], 'settings'),
          },
          {
            name: 'user-roles',
            path: 'user-roles',
            meta: { timer: 3000, title: 'Organization Settings', parentTitle: 'Settings' },
            component: UserRoles,
            beforeEnter: checkPermissionsOrRedirect(authDataStore, ['manageGreenhouseRoles'], 'settings'),
          },
          {
            name: 'custom-fields',
            path: 'custom-fields',
            meta: { timer: 3000, title: 'Organization Settings', parentTitle: 'Settings' },
            component: CustomFields,
            beforeEnter: checkPermissionsOrRedirect(authDataStore, ['manageCustomFields'], 'settings'),
          },
          {
            name: 'ai-settings',
            path: 'ai-settings',
            meta: { timer: 3000, title: 'Organization Settings', parentTitle: 'Settings' },
            component: AiSettings,
            beforeEnter: checkPermissionsOrRedirect(authDataStore, ['configureAi'], 'settings'),
          },
          {
            name: 'job-post-settings',
            path: 'job-post-settings',
            meta: { timer: 3000, title: 'Organization Settings', parentTitle: 'Settings' },
            component: JobPostSettings,
            beforeEnter: checkPermissionsOrRedirect(authDataStore, ['manageTeam'], 'settings'),
          },
          {
            name: 'job-profile-settings',
            path: 'job-profile-settings',
            meta: { timer: 3000, title: 'Organization Settings', parentTitle: 'Settings' },
            component: JobProfileSettings,
            beforeEnter: checkPermissionsOrRedirect(authDataStore, ['editJobProfileSettings'], 'settings'),
          },
          {
            name: 'acknowledgements-settings',
            path: 'acknowledgements-settings',
            meta: { timer: 3000, title: 'Organization Settings', parentTitle: 'Settings' },
            component: AcknowledgementSettings,
            beforeEnter: checkPermissionsOrRedirect(authDataStore, ['editAcknowledgementsSettings'], 'settings'),
          },
          {
            name: 'flsa-settings',
            path: 'flsa-settings',
            meta: { timer: 3000, title: 'Organization Settings', parentTitle: 'Settings' },
            component: FlsaSettings,
            beforeEnter: checkPermissionsOrRedirect(authDataStore, ['editFlsaSettings'], 'settings'),
          },
        ],
      },
      {
        name: 'user-roles-old',
        path: 'user-roles',
        redirect: () => ({ name: 'user-roles' }),
      },
      {
        name: 'custom-fields-old',
        path: 'custom-fields',
        redirect: () => ({ name: 'custom-fields' }),
      },
      {
        name: 'style-guide',
        path: 'style-guide',
        meta: { timer: 3000, title: 'Style Guide' },
        component: StyleGuide,
        beforeEnter: checkPermissionsOrRedirect(authDataStore, ['manageTeam'], 'settings'),
      },
      {
        name: 'policy-guide',
        path: 'policy-guide',
        meta: { timer: 3000, title: 'Policy Guide' },
        component: PolicyGuide,
        beforeEnter: checkPermissionsOrRedirect(authDataStore, ['manageTeam'], 'settings'),
      },
    ],
  },
]);
